/**
 * @fileoverview User Management Utilities
 * Functions for managing user permissions, tool access, divisions, groups, and manager status
 */

import { getFirestore, doc, setDoc, updateDoc, getDoc, getDocs, collection, query, where, deleteDoc } from 'firebase/firestore'
import {
  TOOLS_CONFIG,
  ROLES_CONFIG,
  getDivisions,
  getGroupsForDivision,
  getAllGroups,
  getToolsForGroup,
  getAllTools,
  getToolById,
  getDivisionName,
  getGroupName,
  userHasToolAccess,
  userCanManageGroup,
  getUserAccessibleTools,
  generateUserMenu,
  // Legacy exports for compatibility
  ORGANIZATION_CONFIG,
  AVAILABLE_TOOLS,
  AVAILABLE_ROLES,
  DIVISIONS_AND_GROUPS
} from '@/config/toolsConfig'

// Get all pending users (invited but not logged in yet)
export async function getPendingUsers() {
  const db = getFirestore()
  const usersRef = collection(db, 'users')
  const q = query(usersRef, where('uid', '==', null))
  const snapshot = await getDocs(q)
  return snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }))
}

// Get all users (pending and active)
export async function getAllUsers() {
  const db = getFirestore()
  const usersRef = collection(db, 'users')
  const snapshot = await getDocs(usersRef)
  return snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }))
}

// Check if user exists (pending or active)
export async function userExists(email) {
  const db = getFirestore()
  const userRef = doc(db, 'users', email)
  const userDoc = await getDoc(userRef)
  return userDoc.exists()
}

// Get user by email
export async function getUserByEmail(email) {
  const db = getFirestore()
  const userRef = doc(db, 'users', email)
  const userDoc = await getDoc(userRef)
  
  if (userDoc.exists()) {
    return { ...userDoc.data(), id: userDoc.id }
  }
  return null
}

// Admin function to invite/pre-configure a user
export async function inviteUser(email, role, toolPermissions, divisions, groups, invitedBy) {
  const db = getFirestore()
  const userRef = doc(db, 'users', email)
  
  // Check if user already exists
  const existingDoc = await getDoc(userRef)
  if (existingDoc.exists()) {
    throw new Error('User already exists')
  }
  
  // Auto-generate manager status based on role
  const groupsArray = Array.isArray(groups) ? groups : [groups]
  const managerStatus = {}
  if (role === 'Manager') {
    // Managers are automatically managers of all their assigned groups
    groupsArray.forEach(groupId => {
      managerStatus[groupId] = true
    })
  }
  
  await setDoc(userRef, {
    email: email,
    uid: null,                    // No UID yet - they haven't logged in
    role: role || 'Employee',     // Single role field
    divisions: Array.isArray(divisions) ? divisions : [divisions], // Multiple divisions support
    groups: groupsArray,          // Multiple groups support
    // Legacy support for single division/group (for backward compatibility)
    division: Array.isArray(divisions) ? divisions[0] : divisions,
    group: groupsArray[0],
    manager_status: managerStatus, // Auto-generated based on role
    tool_permissions: toolPermissions,
    profile: {},
    invited_by: invitedBy,
    invited_at: new Date(),
    created: null,                // Will be set when they first log in
    last_login: null
  })
  
  return { success: true, email }
}

// Grant tool access and/or manager permissions
export async function updateToolPermissions(email, toolName, access, manager = false) {
  const db = getFirestore()
  const userRef = doc(db, 'users', email)
  
  const updateData = {}
  updateData[`tool_permissions.${toolName}`] = {
    access: access,
    manager: manager
  }
  
  await updateDoc(userRef, updateData)
  return { success: true, email, tool: toolName, access, manager }
}

// Update user role (single role)
export async function updateUserRoles(email, role) {
  const db = getFirestore()
  const userRef = doc(db, 'users', email)
  
  await updateDoc(userRef, { role })
  return { success: true, email, role }
}

// Update user division (single - legacy)
export async function updateUserDivision(email, division) {
  const db = getFirestore()
  const userRef = doc(db, 'users', email)
  
  await updateDoc(userRef, { 
    division,
    divisions: [division] // Update both for consistency
  })
  return { success: true, email, division }
}

// Update user group (single - legacy)
export async function updateUserGroup(email, group) {
  const db = getFirestore()
  const userRef = doc(db, 'users', email)
  
  await updateDoc(userRef, { 
    group,
    groups: [group] // Update both for consistency
  })
  return { success: true, email, group }
}

// Update user divisions (multiple)
export async function updateUserDivisions(email, divisions) {
  const db = getFirestore()
  const userRef = doc(db, 'users', email)
  
  const divisionsArray = Array.isArray(divisions) ? divisions : [divisions]
  await updateDoc(userRef, { 
    divisions: divisionsArray,
    division: divisionsArray[0] || null // Keep legacy field updated
  })
  return { success: true, email, divisions: divisionsArray }
}

// Update user groups (multiple)
export async function updateUserGroups(email, groups) {
  const db = getFirestore()
  const userRef = doc(db, 'users', email)
  
  const groupsArray = Array.isArray(groups) ? groups : [groups]
  await updateDoc(userRef, { 
    groups: groupsArray,
    group: groupsArray[0] || null // Keep legacy field updated
  })
  return { success: true, email, groups: groupsArray }
}

// Update manager status for specific groups
export async function updateManagerStatus(email, groupId, isManager) {
  const db = getFirestore()
  const userRef = doc(db, 'users', email)
  
  const updateData = {}
  updateData[`manager_status.${groupId}`] = isManager
  
  await updateDoc(userRef, updateData)
  return { success: true, email, group: groupId, manager: isManager }
}

// Deactivate user
export async function deactivateUser(email) {
  const db = getFirestore()
  const userRef = doc(db, 'users', email)
  
  await updateDoc(userRef, { status: 'inactive' })
  return { success: true, email, status: 'inactive' }
}

// Reactivate user
export async function reactivateUser(email) {
  const db = getFirestore()
  const userRef = doc(db, 'users', email)
  
  await updateDoc(userRef, { status: 'active' })
  return { success: true, email, status: 'active' }
}

// Delete user (remove from system entirely)
export async function deleteUser(email) {
  const db = getFirestore()
  const userRef = doc(db, 'users', email)
  
  await deleteDoc(userRef)
  return { success: true, email, deleted: true }
}

// Get all managers for a specific group
export async function getGroupManagers(groupId) {
  const db = getFirestore()
  const usersRef = collection(db, 'users')
  const q = query(usersRef, where('status', '==', 'active'))
  const snapshot = await getDocs(q)
  
  const managers = []
  snapshot.docs.forEach(doc => {
    const userData = doc.data()
    if (userData.manager_status?.[groupId] === true) {
      managers.push({ ...userData, id: doc.id })
    }
  })
  
  return managers
}

// Check if a user is a manager for a specific group
export async function isGroupManager(email, groupId) {
  const user = await getUserByEmail(email)
  if (!user) return false
  
  // If user has Manager role and is assigned to this group, they are a manager
  return user.role === 'Manager' && user.groups && user.groups.includes(groupId)
}

// Get all groups that a user manages
export async function getUserManagedGroups(email) {
  const user = await getUserByEmail(email)
  if (!user) return []
  
  // If user has Manager role, they manage all their assigned groups
  if (user.role === 'Manager' && user.groups) {
    return user.groups
  }
  
  return []
}

// Get users by division
export async function getUsersByDivision(division) {
  const db = getFirestore()
  const usersRef = collection(db, 'users')
  const q = query(usersRef, where('division', '==', division))
  const snapshot = await getDocs(q)
  return snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }))
}

// Get users by group
export async function getUsersByGroup(group) {
  const db = getFirestore()
  const usersRef = collection(db, 'users')
  const q = query(usersRef, where('group', '==', group))
  const snapshot = await getDocs(q)
  return snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }))
}

// Get users with access to a specific tool
export async function getUsersWithToolAccess(toolName) {
  const db = getFirestore()
  const usersRef = collection(db, 'users')
  const snapshot = await getDocs(usersRef)
  
  const usersWithAccess = []
  snapshot.docs.forEach(doc => {
    const userData = doc.data()
    if (userHasToolAccess(userData, toolName)) {
      usersWithAccess.push({ ...userData, id: doc.id })
    }
  })
  
  return usersWithAccess
}

// Bulk invite users from a list
export async function bulkInviteUsers(userList, invitedBy) {
  const results = []
  
  for (const userInfo of userList) {
    try {
      const result = await inviteUser(
        userInfo.email,
        userInfo.roles || ['Employee'],
        userInfo.tool_permissions || {},
        userInfo.division || 'general_counsel',
        userInfo.group || 'general_counsel',
        userInfo.manager_status || {},
        invitedBy
      )
      results.push({ ...result, status: 'success' })
    } catch (error) {
      results.push({ 
        email: userInfo.email, 
        status: 'error', 
        error: error.message 
      })
    }
  }
  
  return results
}

// =============================================================================
// EXPORTED FUNCTIONS AND DATA FROM TOOLS CONFIG
// =============================================================================

// Re-export all the helper functions from the tools config
export {
  getDivisions as getAvailableDivisions,
  getGroupsForDivision,
  getAllGroups,
  getToolsForGroup,
  getAllTools,
  getToolById,
  getDivisionName,
  getGroupName,
  userHasToolAccess,
  userCanManageGroup,
  getUserAccessibleTools,
  generateUserMenu
}

// Re-export configuration data
export {
  TOOLS_CONFIG,
  ROLES_CONFIG,
  ORGANIZATION_CONFIG,
  AVAILABLE_TOOLS,
  AVAILABLE_ROLES,
  DIVISIONS_AND_GROUPS
}

// Legacy compatibility - keeping old exports but marking as deprecated
export const AVAILABLE_DEPARTMENTS = getAllGroups().map(group => group.name)